import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface ArchiveKwebcastVodProps extends SharedMenuItemProps {
    entryId: string;
    url: string; // URL for the API endpoint
}

export interface ArchiveKwebcastVodModalProps extends ArchiveKwebcastVodProps {
    isOpen: boolean;
}

/**
 * Detach recording modal
 */
const ArchiveKwebcastVodModal = (props: ArchiveKwebcastVodModalProps) => {
    const { isOpen, entryId, onCloseMenu, url, onItemStateChange } = props;
    const [processing, setProcessing] = useState<boolean>(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const handleCancel = () => {
        sendButtonAnalytics("Detach recording - Cancel", ButtonClickAnalyticsType.CLOSE);
        onCloseMenu?.();
    };

    const handleDetach = async () => {
        setProcessing(true);
        sendButtonAnalytics("Detach recording - Approve", ButtonClickAnalyticsType.EXPORT);

        try {
            const { success, error } = await postKmsData(url, { entryId: entryId });
            if (success) {
                // show success toast
                showToast({ severity: "success", message: translate("Recording detached successfully") });
                // remove the menu item from the entry actions menu
                onItemStateChange?.("recordingDetached", true);
            }
            else {
                // show error toast
                showToast({ severity: "error", message: translate("Failed detaching recording") });
            }
        }
        catch (e: unknown) {
            console.error(e);
            showToast({
                severity: "error",
                message: translate("An error occurred while detaching the recording"),
            });
        }

        setProcessing(false);

        onCloseMenu?.();
    };

    return (
        <Modal open={isOpen}>
            <ModalTitle>
                <Typography variant={"h3"}>{translate("Detach recording")}</Typography>
            </ModalTitle>
            <ModalContent>
                <Typography align="center">
                    {translate("Are you sure you want to detach the recording from live session?")}
                </Typography>
                <Typography align="center">
                    {translate(
                        "The live session page will move back to lobby state, and the recording will be available for users as a video media."
                    )}
                </Typography>
            </ModalContent>
            <ModalActions>
                <Button variant={"borderless"} color={"secondary"} onClick={handleCancel}>
                    {translate("Cancel")}
                </Button>
                <Button color="danger" onClick={handleDetach} loading={processing}>
                    {translate("Detach")}
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default ArchiveKwebcastVodModal;
